import { useEffect, useState } from 'react';

import { useGetApiV1PersonSharedAssociatedCompanies } from '@/shared/api/generated';

export interface DropdownOption {
  readonly id: string;
  readonly name: string;
}

/**
 * Busca server-side de empresas por nome, com debounce de 1 s e mínimo de 3 caracteres.
 *
 * @param filter - Texto digitado pelo usuário no campo de busca (default: `''`).
 * @returns Lista de empresas com estado de loading e sucesso da query.
 *
 * @example
 * ```tsx
 * const { companies, isLoading, isQuerySuccess } = useCompaniesDropdown(companySearch);
 * ```
 */
export function useCompaniesDropdown(filter = '') {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedFilter(filter), 1000);
    return () => clearTimeout(timer);
  }, [filter]);

  const isReady = debouncedFilter.length >= 3;

  const { data, isLoading, isSuccess } = useGetApiV1PersonSharedAssociatedCompanies(
    isReady ? { filter: debouncedFilter } : undefined,
    { query: { enabled: isReady } },
  );

  const companies: readonly DropdownOption[] = (data ?? []).map(item => ({
    id: String(item.key ?? ''),
    name: item.value ?? '',
  }));

  return {
    companies,
    isLoading,
    isQuerySuccess: isSuccess,
  } as const;
}
